
import type { AsMutableArray, MutableOrImmutableArray, ToMutableOrImmutableArray } from "./array-checks.js";
import type { DefaultIfNever } from "./conditional.js";

type NestedPropsPathBuilder<
  T,
  TPATH extends MutableOrImmutableArray<PropertyKey>,
  BuiltPath extends PropertyKey[] = []
> =
  AsMutableArray<TPATH> extends [ infer CURRENT_PROP extends PropertyKey, ...infer REMAINING_PROPS extends PropertyKey[] ]
  ? (
    CURRENT_PROP extends keyof T
    ? NestedPropsPathBuilder<T[ CURRENT_PROP ], REMAINING_PROPS, [ ...BuiltPath, CURRENT_PROP ]>
    : { Result: "Failure", BuiltPath: BuiltPath, ValueType: T, FAILED_PROPS: [ CURRENT_PROP, ...REMAINING_PROPS ] }
  )
  : { Result: "Success", BuiltPath: BuiltPath, ValueType: T };

type SuccessResult = { Result: "Success", BuiltPath: PropertyKey[], ValueType: any };
type FailureResult = { Result: "Failure", BuiltPath: PropertyKey[], ValueType: any, FAILED_PROPS: PropertyKey[] };

type NestedPropsPathBuilderResult = SuccessResult | FailureResult;

type NestedPropsPathBuilderHelper<
  T,
  TPATH extends MutableOrImmutableArray<PropertyKey>,
  SuccessProp extends keyof SuccessResult,
  FailureProp extends keyof FailureResult | null,
  TResult extends NestedPropsPathBuilderResult = NestedPropsPathBuilder<T, TPATH>
> =
  TResult extends SuccessResult
  ? TResult[ SuccessProp ] :
  TResult extends FailureResult
  ? (FailureProp extends null ? never : TResult[ Exclude<FailureProp, null> ])
  : never;

export type NestedPropsPath<T, TPATH extends ToMutableOrImmutableArray<[ PropertyKey, ...PropertyKey[] ]>> = NestedPropsPathBuilderHelper<T, TPATH, "BuiltPath", null>;
export type NestedPropsPathValue<T, TPATH extends ToMutableOrImmutableArray<[ PropertyKey, ...PropertyKey[] ]>> = NestedPropsPathBuilderHelper<T, TPATH, "ValueType", null>;


export function getValueFromPropsPath<T, TPath extends ToMutableOrImmutableArray<[ PropertyKey, ...PropertyKey[] ]>, TDefault> (
  item: T,
  path: TPath,
  valueIfPathInvalid?: TDefault
): DefaultIfNever<NestedPropsPathValue<T, TPath>, TDefault>;

export function getValueFromPropsPath(item, path, valueIfPathInvalid) {
  let curValue = item;
  for (const prop of path) {
    if (prop in curValue) {
      curValue = item[prop];
    }
    else {
      return valueIfPathInvalid;
    }
  }
  return curValue;
}